import { Button } from "flowbite-react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { BASE_URL } from "@/Store/Action";
import { getWithExpiry } from "@/utils/token";
import { UserReservationListGet200Response } from "@models/UserReservationListGet200Response";
import { ModelUserReservationList } from "@models/ModelUserReservationList";
import { ModelSubmitReservationUserDTO } from "@models/ModelSubmitReservationUserDTO";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const daysOfWeek = ["일", "월", "화", "수", "목", "금", "토"];
function Reservation({
  onPrev,
  onNext,
  guideline,
}: {
  onPrev: () => void;
  onNext: (data: any) => void;
  guideline: any;
}) {
  const [selectedDate, setSelectedDate] = useState<string>(
    guideline?.review_start_date
      ? dayjs(guideline.review_start_date, "YY-MM-DD").format("YYYY-MM-DD")
      : ""
  );
  const { secretkey } = useParams<{ secretkey: string }>();
  const [selectedTime, setSelectedTime] = useState<string>("");
  const startDate = dayjs(guideline?.review_start_date, "YY-MM-DD");
  const endDate = dayjs(guideline?.review_end_date, "YY-MM-DD");
  const [currentMonth, setCurrentMonth] = useState<number>(startDate.month());
  const [currentYear, setCurrentYear] = useState<number>(startDate.year());
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [reservationData, setReservationData] = useState<
    ModelUserReservationList[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAvailableData, setHasAvailableData] = useState<boolean>(true);
  const [hasReservation, setHasReservation] = useState<boolean>(true);
  useEffect(() => {
    if (guideline) {
      setSelectedTime("");
      setShowConfirm(false);
      setError(null);
      setHasAvailableData(true);
      setIsLoading(true);

      const fallbackStartDate = dayjs(guideline.review_start_date, "YY-MM-DD");
      setCurrentMonth(fallbackStartDate.month());
      setCurrentYear(fallbackStartDate.year());

      setSelectedDate(fallbackStartDate.format("YYYY-MM-DD")); // set dulu defaultnya
      setHasReservation(guideline.is_reservation === true);

      fetchDataCalendar(secretkey ?? "".toString());
    }
  }, [, guideline]);
  useEffect(() => {
    if (!reservationData?.length) return;

    const validDate = reservationData
      .filter(
        (r) =>
          r.is_active &&
          r.time_list?.some(
            (t) =>
              t.visible === 1 &&
              t.apply != null &&
              t.slot != null &&
              t.apply < t.slot
          )
      )
      .sort((a, b) => dayjs(a.rsv_date).diff(dayjs(b.rsv_date)))[0];

    if (validDate?.rsv_date) {
      setSelectedDate(validDate.rsv_date);
    }
  }, [reservationData]);

  const fetchDataCalendar = async (secretkey: string) => {
    try {
      const response = await fetch(
        `${BASE_URL}/wg/reservation?secret_key=${secretkey}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const json: UserReservationListGet200Response = await response.json();

      if (json.meta?.code === 200) {
        const transformedData =
          json.data?.map((item) => ({
            ...item,
            rsv_date: dayjs(item.rsv_date).format("YYYY-MM-DD"),
          })) || [];

        setReservationData(transformedData);

        const hasData = transformedData.some(
          (item) =>
            item.is_active &&
            item.time_list?.some(
              (time) =>
                time.visible === 1 &&
                time.slot != null &&
                time.apply != null &&
                time.slot > time.apply
            )
        );

        setHasAvailableData(hasData);
      } else {
        throw new Error(
          json.meta?.message || "Failed to fetch reservation data"
        );
      }
    } catch (error) {
      console.error("Failed to fetch calendar data:", error);
      setError("예약 가능한 시간을 불러오는데 실패했습니다.");
      setHasAvailableData(false);
    } finally {
      setIsLoading(false);
    }
  };

  const getAvailableTimesForDate = (date: string) => {
    if (!date) return [];
    const formattedDate = dayjs(date).format("YYYY-MM-DD");
    const found = reservationData.find(
      (r) => r.rsv_date === formattedDate && r.is_active
    );
    return (
      found?.time_list?.filter(
        (t) =>
          t.visible === 1 &&
          t.slot != null &&
          t.apply != null &&
          t.slot > t.apply
      ) || []
    );
  };
const handleClick = () => {
  if (!selectedDate || !selectedTime) return;
  handleReservation();  
};

  const handleReservation = () => {
  if (!selectedDate || !selectedTime) {
    alert("날짜와 시간을 선택해주세요.");
    return;
  }
  setShowConfirm(true);
};

const confirmReservation = async () => {
    try {
      const formattedDate = dayjs(selectedDate).format("YYYY-MM-DD");
      const selected = reservationData
        .find((r) => r.rsv_date === formattedDate)
        ?.time_list?.find((t) => t.time === selectedTime);

      if (!selected) {
        Swal.fire({
          icon: "warning",
          title: "예약 ID 없음",
          text: "선택된 시간의 예약 ID를 찾을 수 없습니다.",
          confirmButtonColor: "#0F1E6C", // Warna biru gelap sesuai tema
          buttonsStyling: false,
          customClass: {
            confirmButton: "bg-[#0F1E6C] text-white font-medium py-2 px-4 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1E6C] focus:ring-opacity-50 hover:bg-[#2d3d74] transition-colors"
          }
        });
        return;
      }

      const payload = {
        rsv_id: selected.id!,
        secret_key: secretkey!,
      };
      const response = await fetch(`${BASE_URL}/wg/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.meta?.code === 200) {
        setShowConfirm(false);
        
        await Swal.fire({
          icon: "success",
          title: "예약 완료",
          text: "예약이 완료되었습니다! 예약 시간에 매장에 방문해 주세요.",
          confirmButtonColor: "#0F1E6C",
          buttonsStyling: false,
          customClass: {
            confirmButton: "bg-[#0F1E6C] text-white font-medium py-2 px-4 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1E6C] focus:ring-opacity-50 hover:bg-[#2d3d74] transition-colors"
          }
        });

        onNext({
          ...guideline,
          is_reservation: true,
          reservation_dtm: `${selectedDate} ${selectedTime}`,
        });
        
      } else {
        throw new Error(result.meta?.message || "예약 실패");
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "오류 발생",
        text: "예약에 실패했습니다. 다시 시도해 주세요.",
        confirmButtonColor: "#0F1E6C",
        buttonsStyling: false,
        customClass: {
          confirmButton: "bg-[#0F1E6C] text-white font-medium py-2 px-4 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1E6C] focus:ring-opacity-50 hover:bg-[#2d3d74] transition-colors"
        }
      });
    }
  };
  const generateCalendarDays = () => {
    const days = [];
    const firstDayOfMonth = dayjs(
      `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-01`
    );
    const startDay = firstDayOfMonth.day();
    const daysInMonth = firstDayOfMonth.daysInMonth();

    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = dayjs(
        `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(
          day
        ).padStart(2, "0")}`
      );
      const dateStr = date.format("YYYY-MM-DD");

      const isWithinRange =
        date.isSameOrAfter(startDate, "day") &&
        date.isSameOrBefore(endDate, "day");

      const reservationForDate = reservationData.find(
        (r) => r.rsv_date === dateStr
      );
      const hasAvailableSlots =
        reservationForDate?.is_active === true &&
        reservationForDate.time_list?.some(
          (t) => t.visible === 1 && (t.apply ?? 0) < (t.slot ?? 0)
        );

      days.push(
        <div className="flex items-center justify-center">
          <button
            key={day}
            onClick={() => {
              if (isWithinRange && hasAvailableSlots) {
                setSelectedDate(dateStr);
                setSelectedTime("");
              }
            }}
            className={`w-9 h-9 rounded-full text-xs 
                ${
                  selectedDate === dateStr
                    ? "bg-[#0F1E6C] text-white"
                    : "bg-white"
                }
                ${
                  !isWithinRange
                    ? "text-gray-300 cursor-not-allowed"
                    : hasAvailableSlots
                    ? "border border-gray-300 cursor-pointer"
                    : "text-gray-400 cursor-not-allowed"
                }`}
            disabled={!isWithinRange || !hasAvailableSlots}
            title={
              !isWithinRange
                ? "예약 기간이 아닙니다"
                : !reservationForDate?.is_active
                ? "비활성화된 날짜입니다"
                : reservationForDate?.time_list?.every((t) => t.visible !== 1)
                ? "보이는 시간이 없습니다"
                : reservationForDate?.time_list?.every(
                    (t) => (t.apply ?? 0) >= (t.slot ?? 0)
                  )
                ? "모든 시간이 마감되었습니다"
                : ""
            }
          >
            {day}
          </button>
        </div>
      );
    }
    return days;
  };

  const handlePrevMonth = () => {
    const newDate = dayjs()
      .year(currentYear)
      .month(currentMonth)
      .subtract(1, "month");
    setCurrentMonth(newDate.month());
    setCurrentYear(newDate.year());
  };

  const handleNextMonth = () => {
    const newDate = dayjs()
      .year(currentYear)
      .month(currentMonth)
      .add(1, "month");
    setCurrentMonth(newDate.month());
    setCurrentYear(newDate.year());
  };

  const isPrevMonthDisabled = () => {
    const prevMonth = dayjs()
      .year(currentYear)
      .month(currentMonth)
      .subtract(1, "month");
    return prevMonth.isBefore(startDate, "month");
  };

  const isNextMonthDisabled = () => {
    const nextMonth = dayjs()
      .year(currentYear)
      .month(currentMonth)
      .add(1, "month");
    return nextMonth.isAfter(endDate, "month");
  };

  if (!guideline) return null;

  return (
    <>
      <div className="">
        <div className="bg-white  relative ">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
              <span className="text-xs text-gray-600">
                예약 가능한 시간대를 확인하고 있습니다...
              </span>
            </div>
          ) : error ? (
            <div className="py-10 text-red-500">
              {error}
              <button
                onClick={() => {
                  setIsLoading(true);
                  fetchDataCalendar(guideline.campaign_id.toString());
                }}
                className="mt-2 px-4 py-2 bg-gray-100 rounded-md text-xs"
              >
                다시 시도하기
              </button>
            </div>
          ) : (
            <>
              <div className="mb-4">
                {/* Title */}
                <div className="border pb-2 mb-5 flex flex-col gap-1 ps-3 bg-gray-100 rounded-lg pt-5">
                  <h2 className="text-sm font-bold text-gray-800 mb-1">
                    체험 날짜와 시간을 선택해 주세요
                  </h2>
                  <p className="text-xs text-gray-400 mb-4">
                    선택하신 날짜와 시간에 꼭 방문해 주세요.
                  </p>
                </div>
                <div className="px-2">
                  <div className="flex gap-4 items-center mb-4 pt-2">
                    <ChevronLeft
                      onClick={() => {
                        if (!isPrevMonthDisabled()) {
                          handlePrevMonth();
                        }
                      }}
                      className={`cursor-pointer ${
                        isPrevMonthDisabled()
                          ? "text-gray-300 cursor-not-allowed pointer-events-none"
                          : "hover:text-[#0F1E6C]"
                      }`}
                    />

                    <h3 className="text-xs font-semibold">
                      {currentYear}년 {currentMonth + 1}월
                    </h3>
                    <ChevronRight
                      onClick={() => {
                        if (!isNextMonthDisabled()) {
                          handleNextMonth();
                        }
                      }}
                      className={`cursor-pointer ${
                        isNextMonthDisabled()
                          ? "text-gray-300 cursor-not-allowed pointer-events-none"
                          : "hover:text-[#0F1E6C]"
                      }`}
                    />
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-xs font-medium bg-gray-100 py-1 mb-2">
                    {daysOfWeek.map((day) => (
                      <div key={day} className="text-center py-1">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1 items-center justify-center">
                    {generateCalendarDays()}
                  </div>
                </div>
              </div>

              <div className="mt-4 px-3 mb-2">
                <h3 className="text-xs text-left text-gray-700 font-semibold my-2">
                  시간 선택{" "}
                  {selectedDate &&
                    `(${dayjs(selectedDate).format("MM월 DD일")})`}
                </h3>
                <div className=" ">
                  {selectedDate ? (
                    getAvailableTimesForDate(selectedDate).length > 0 ? (
                      <div className="grid grid-cols-4 gap-2">
                        {getAvailableTimesForDate(selectedDate)
                          .filter((t) => t.time !== undefined)
                          .map((t) => (
                            <button
                              key={t.id}
                              onClick={() => setSelectedTime(t.time!)}
                              className={`py-2 text-xs rounded-full border transition-colors
                              ${
                                selectedTime === t.time
                                  ? "bg-[#0F1E6C] text-white border-[#0F1E6C]"
                                  : "bg-white border-gray-300 hover:bg-gray-50"
                              }`}
                            >
                              {t.time!.slice(0, 5)}
                            </button>
                          ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 py-4">
                        이 날짜는 예약이 마감되었습니다. 다른 날짜를 선택해 주세요.
                      </p>
                    )
                  ) : (
                    <p className="text-xs text-gray-400 py-4">
                      예약 가능한 날짜를 먼저 선택해주세요.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {showConfirm && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-30">
            <div className="bg-white p-5 rounded-lg shadow-lg w-[90%] max-w-sm text-center">
              <div className="flex justify-end">
                <X
                  onClick={() => setShowConfirm(false)}
                  className="w-5 h-5 text-gray-500 cursor-pointer"
                />
              </div>
              <h2 className=" text-lg font-bold mb-2 pb-2">[ 예약 확인 ]</h2>
              <hr />
              <p className="font-bold mb-1 pt-8 ">
                예약 날짜 및 시간을 확인해주세요.
              </p>
              <p className="text-gray-600 mb-2 text-sm pt-2">
                {dayjs(selectedDate).format("YYYY년 MM월 DD일")},{" "}
                {selectedTime.slice(0, 5)}
              </p>

              <div className="flex justify-center gap-4 mt-8 text-sm">
                <button
                  className="px-4 py-3 w-full text-white bg-gray-300 rounded-full font-semibold"
                  onClick={() => setShowConfirm(false)}
                >
                  취소
                </button>
                <button
                  className="px-4 py-3 w-full text-white bg-[#0F1E6C] rounded-full font-semibold"
                  onClick={confirmReservation}
                >
                  예약하기
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Fixed Button */}
      <div className="max-w-xl mx-auto fixed z-20 bottom-0 left-0 right-0 bg-white border-t px-4 py-3 flex gap-2">
        <div
          onClick={onPrev}
          className="flex pr-2 text-sm rounded-full w-1/4 bg-[#F4F5FF] text-[#0F1E6C] h-11 justify-center items-center"
        >
          <ChevronLeft className=""></ChevronLeft>
          <div className="font-semibold"> 이전</div>
        </div>

        <Button
          className={`rounded-full w-3/4 bg-[#0F1E6C] text-white text-base h-11
                  ${
                    selectedDate && selectedTime
                      ? "bg-[#0F1E6C] hover:bg-[#2d3d74]"
                      : "bg-gray-500 cursor-not-allowed"
                  }`}
          onClick={handleClick}
          disabled={!selectedDate || !selectedTime}
        >
          예약하기
        </Button>
      </div>
    </>
  );
}

export default Reservation;
